import { useVirtualizer } from '@tanstack/react-virtual'
import { memo, useEffect, useRef, useState } from 'react'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'

import { ScrollArea } from '../ui'
import { ProductCard } from './ProductCard'

type ProductGridProps = {
  product_items?: {
    product_id?: number
    product_variant_color_id?: number
    relevance_score?: number
  }[]
  height?: string
  scrollArea?: boolean
  numColumns?: number
  layout?: 'masonry' | 'row'
  isChat?: boolean
  showAddToQuote?: boolean
  showReorder?: boolean
  addToCurrentQuoteId?: number
  /** When set, org favourite toggle/check uses this org (e.g. admin viewing another org’s favourites). */
  favouritesOrgId?: string | null
}

const ProductGridComponent = ({
  product_items = [],
  height,
  scrollArea = true,
  numColumns = 4,
  layout = 'masonry',
  isChat = false,
  showAddToQuote = true,
  showReorder = false,
  addToCurrentQuoteId,
  favouritesOrgId,
}: ProductGridProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null)

  // Find the ScrollArea viewport element
  useEffect(() => {
    if (scrollAreaRef.current) {
      const viewport = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]'
      ) as HTMLElement
      if (viewport) {
        setScrollElement(viewport)
      }
    }
  }, [])

  // Estimate card height - ProductCard has min-h-[350px] plus margins
  const estimatedCardHeight = 450

  const virtualizer = useVirtualizer({
    count: product_items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimatedCardHeight,
    overscan: 5,
  })

  // Get visible range
  const virtualItems = virtualizer.getVirtualItems()
  const visibleIndices = new Set(virtualItems.map((item) => item.index))

  // Only use virtualization if scroll element is available and we have items
  const useVirtualization = scrollElement !== null && product_items.length > 0

  const Content = () => {
    if (layout === 'row') {
      return (
        <div className="w-full overflow-x-auto">
          <div className="flex gap-4 pb-4" style={{ width: 'max-content' }}>
            {product_items.map((product_item) => {
              return (
                <div
                  key={`${product_item.product_id}`}
                  className="flex-shrink-0"
                  style={{ width: '250px' }}
                >
                  <ProductCard
                    product_item={product_item}
                    numColumns={numColumns}
                    isChat={isChat}
                    showAddToQuote={showAddToQuote}
                    showReorder={showReorder}
                    addToCurrentQuoteId={addToCurrentQuoteId}
                    favouritesOrgId={favouritesOrgId}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    return (
      <div className="w-full">
        <ResponsiveMasonry
          columnsCountBreakPoints={{
            350: 1,
            900: numColumns,
          }}
          gutterBreakPoints={{
            350: '16px',
          }}
        >
          <Masonry>
            {useVirtualization
              ? product_items.map((product_item, index) => {
                  // Only render ProductCard if it's in the visible range
                  if (visibleIndices.has(index)) {
                    return (
                      <ProductCard
                        key={`${product_item.product_id}`}
                        product_item={product_item}
                        isChat={isChat}
                        showAddToQuote={showAddToQuote}
                        showReorder={showReorder}
                        addToCurrentQuoteId={addToCurrentQuoteId}
                        favouritesOrgId={favouritesOrgId}
                      />
                    )
                  }
                  // Render placeholder div to maintain masonry layout
                  return (
                    <div
                      key={`placeholder-${product_item.product_id}`}
                      style={{
                        height: estimatedCardHeight,
                        width: '100%',
                      }}
                    />
                  )
                })
              : // Fallback: render all items if virtualization isn't ready
                product_items.map((product_item) => {
                  return (
                    <ProductCard
                      key={`${product_item.product_id}`}
                      product_item={product_item}
                      numColumns={numColumns}
                      isChat={isChat}
                      showAddToQuote={showAddToQuote}
                      showReorder={showReorder}
                      addToCurrentQuoteId={addToCurrentQuoteId}
                      favouritesOrgId={favouritesOrgId}
                    />
                  )
                })}
          </Masonry>
        </ResponsiveMasonry>
      </div>
    )
  }

  if (!scrollArea) {
    return <Content />
  }

  return (
    <ScrollArea ref={scrollAreaRef} className={`${height || 'h-full'} w-full`}>
      <Content />
    </ScrollArea>
  )
}

// Comparison function to check if product_items have changed
const areProductItemsEqual = (
  prevProps: ProductGridProps,
  nextProps: ProductGridProps
) => {
  const prevItems = prevProps.product_items || []
  const nextItems = nextProps.product_items || []

  // If lengths differ, they're not equal
  if (prevItems.length !== nextItems.length) {
    return false
  }

  // Compare each item's product_id and product_variant_color_id
  for (let i = 0; i < prevItems.length; i++) {
    const prevItem = prevItems[i]
    const nextItem = nextItems[i]

    if (
      prevItem?.product_id !== nextItem?.product_id ||
      prevItem?.product_variant_color_id !==
        nextItem?.product_variant_color_id ||
      prevItem?.relevance_score !== nextItem?.relevance_score
    ) {
      return false
    }
  }

  // Check other props that should trigger re-render
  return (
    prevProps.height === nextProps.height &&
    prevProps.scrollArea === nextProps.scrollArea &&
    prevProps.layout === nextProps.layout &&
    prevProps.numColumns === nextProps.numColumns &&
    prevProps.showAddToQuote === nextProps.showAddToQuote &&
    prevProps.showReorder === nextProps.showReorder &&
    prevProps.addToCurrentQuoteId === nextProps.addToCurrentQuoteId &&
    prevProps.favouritesOrgId === nextProps.favouritesOrgId
  )
}

export const ProductGrid = memo(ProductGridComponent, areProductItemsEqual)
